/**
 * Shopify Rewards Button - Standalone Bundle
 * 
 * A simple floating button that can trigger your rewards widget
 * 
 * Usage:
 * 1. Add this script to your theme.liquid before </body>
 * 2. Configure RewardsButtonConfig
 * 3. The button will appear automatically
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  window.RewardsButtonConfig = window.RewardsButtonConfig || {
    // Button Text
    text: 'Earn ₹200',
    
    // Button Position: 'right' or 'left'
    position: 'right',
    
    // Button Color (hex code)
    backgroundColor: '#ffffff',
    textColor: '#111111',
    
    // Custom CSS class (optional)
    customClass: '',
    
    // Callback function when button is clicked
    onClick: function() {
      // Default: Try to trigger rewards widget if available
      if (window.RewardsWidget && window.rewardsWidgetInstance) {
        // If widget is already initialized, open it
        if (window.rewardsWidgetInstance.openModal) {
          window.rewardsWidgetInstance.openModal();
        }
      } else if (window.RewardsConfig) {
        // If config exists, try to initialize widget
        console.log('Rewards widget config found. Initialize widget to use button.');
      } else {
        console.log('Rewards widget not found. Configure onClick callback.');
      }
    }
  };

  // ============================================
  // STYLES
  // ============================================
  function injectStyles() {
    if (document.getElementById('rewards-button-styles')) {
      return; // Styles already injected
    }

    const style = document.createElement('style');
    style.id = 'rewards-button-styles';
    style.textContent = `
      .rewards-button-standalone {
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: var(--rb-bg, #ffffff);
        color: var(--rb-text, #111111);
        padding: 12px 24px;
        font-weight: 600;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 999px;
        cursor: pointer;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        font-size: 15px;
        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        display: flex;
        align-items: center;
        gap: 8px;
        transform: none;
        writing-mode: horizontal-tb;
        line-height: 1;
      }
      
      .rewards-button-standalone::before {
        content: '🎁';
        font-size: 18px;
        line-height: 1;
      }
      
      .rewards-button-standalone:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      }
      
      .rewards-button-standalone.rewards-button-left {
        right: auto;
        left: 20px;
      }
      
      @media (max-width: 768px) {
        .rewards-button-standalone {
          bottom: 20px;
          right: 20px;
          font-size: 14px;
          padding: 10px 20px;
        }
        
        .rewards-button-standalone.rewards-button-left {
          right: auto;
          left: 20px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  function findAssetUrl(filename) {
    // Try to find asset URL from existing script/link tags
    const scripts = document.querySelectorAll('script[src], link[href]');
    for (let i = 0; i < scripts.length; i++) {
      const url = scripts[i].src || scripts[i].href;
      if (url && url.includes('/assets/') && url.includes(filename)) {
        // Extract base URL
        const baseUrl = url.substring(0, url.lastIndexOf('/assets/') + 8);
        return baseUrl + filename;
      }
    }
    return null;
  }

  // ============================================
  // LOAD REWARDS WIDGET
  // ============================================
  function loadAndOpenRewardsWidget() {
    // Check if widget is already loaded
    if (window.RewardsWidget && window.rewardsWidgetInstance) {
      // Widget exists, trigger modal open
      triggerModalOpen();
      return;
    }
    
    // Check if widget script is already loading
    if (window.__rewardsWidgetLoading) {
      console.log('Rewards widget is already loading...');
      return;
    }
    
    // Check if RewardsConfig exists
    if (!window.RewardsConfig) {
      console.error('RewardsConfig not found. Please configure RewardsConfig before using the button.');
      return;
    }
    
    // Mark as loading
    window.__rewardsWidgetLoading = true;
    
    // Load Cloudflare Turnstile if not loaded
    loadTurnstile();
    
    // Load widget CSS
    loadWidgetCSS();
    
    // Load widget script
    loadWidgetScript();
  }
  
  function loadTurnstile() {
    if (document.querySelector('script[src*="turnstile"]')) {
      return; // Already loaded
    }
    
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.defer = true;
    document.head.appendChild(script);
  }
  
  function loadWidgetCSS() {
    // Check if CSS is already loaded
    if (document.querySelector('link[href*="style.css"]')) {
      return; // Already loaded
    }
    
    // Get CSS URL from config or try to find it
    const cssUrl = window.RewardsButtonConfig?.widgetCSSUrl || 
                   findAssetUrl('style.css') ||
                   '/assets/style.css';
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.onerror = function() {
      console.warn('Failed to load widget CSS from:', cssUrl);
      // Try fallback
      const fallbackLink = document.createElement('link');
      fallbackLink.rel = 'stylesheet';
      fallbackLink.href = '/assets/style.css';
      document.head.appendChild(fallbackLink);
    };
    document.head.appendChild(link);
  }
  
  function loadWidgetScript() {
    // Check if script is already loaded
    if (document.querySelector('script[src*="rewards-widget"]')) {
      waitForWidgetAndOpen();
      return;
    }
    
    // Get script URL from config or try to find it
    const scriptUrl = window.RewardsButtonConfig?.widgetScriptUrl ||
                      findAssetUrl('rewards-widget.iife.js') ||
                      '/assets/rewards-widget.iife.js';
    
    if (document.querySelector('script[src*="rewards-widget"]')) {
      // Script tag exists, wait for it to load
      waitForWidgetAndOpen();
      return;
    }
    
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.defer = true;
    script.onload = function() {
      console.log('Rewards widget script loaded');
      waitForWidgetAndOpen();
    };
    script.onerror = function() {
      console.error('Failed to load rewards widget script from:', scriptUrl);
      window.__rewardsWidgetLoading = false;
      alert('Failed to load rewards widget. Please check if the widget files are uploaded.');
    };
    document.body.appendChild(script);
  }
  
  function waitForWidgetAndOpen() {
    // Wait for RewardsWidget class to be available
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    
    const checkInterval = setInterval(function() {
      attempts++;
      
      if (window.RewardsWidget) {
        clearInterval(checkInterval);
        window.__rewardsWidgetLoading = false;
        
        // Initialize widget if not already initialized
        if (!window.rewardsWidgetInstance) {
          if (window.RewardsConfig) {
            try {
              window.rewardsWidgetInstance = new window.RewardsWidget(window.RewardsConfig);
              console.log('Rewards widget initialized');
            } catch (error) {
              console.error('Error initializing rewards widget:', error);
              return;
            }
          } else {
            console.error('RewardsConfig not found');
            return;
          }
        }
        
        // Small delay to ensure widget is ready
        setTimeout(triggerModalOpen, 100);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        window.__rewardsWidgetLoading = false;
        console.error('RewardsWidget class not found after loading script');
      }
    }, 100);
  }
  
  function triggerModalOpen() {
    // Use the widget's openModal method if available
    if (window.rewardsWidgetInstance && typeof window.rewardsWidgetInstance.openModal === 'function') {
      window.rewardsWidgetInstance.openModal();
      return;
    }
    
    // Fallback: Find the rewards button inside the widget and click it
    const widgetButton = document.querySelector('.rewards-button');
    if (widgetButton) {
      widgetButton.click();
    } else {
      // Wait a bit for widget to render
      setTimeout(function() {
        const widgetButton = document.querySelector('.rewards-button');
        if (widgetButton) {
          widgetButton.click();
        } else {
          console.warn('Could not find rewards button to trigger. Widget may need manual trigger.');
        }
      }, 500);
    }
  }

  // ============================================
  // BUTTON CREATION
  // ============================================
  function createButton() {
    // Remove existing button if any
    const existing = document.getElementById('rewards-button-standalone');
    if (existing) {
      existing.remove();
    }

    const config = window.RewardsButtonConfig;
    
    // Create button element
    const button = document.createElement('button');
    button.id = 'rewards-button-standalone';
    button.className = 'rewards-button-standalone';
    
    // Add position class
    if (config.position === 'left') {
      button.classList.add('rewards-button-left');
    }
    
    // Add custom class if provided
    if (config.customClass) {
      button.classList.add(config.customClass);
    }
    
    // Set text
    button.textContent = config.text || 'Earn ₹200';
    
    // Set colors via CSS variables
    button.style.setProperty('--rb-bg', config.backgroundColor || '#ffffff');
    button.style.setProperty('--rb-text', config.textColor || '#111111');
    
    // Set aria label
    button.setAttribute('aria-label', 'Open Referral Program');
    
    // Add click handler
    button.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Load and open rewards widget
      loadAndOpenRewardsWidget();
      
      // Call custom onClick if provided
      if (typeof config.onClick === 'function') {
        config.onClick();
      }
    });
    
    // Append to body
    document.body.appendChild(button);
    
    console.log('✅ Rewards Button created successfully');
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    // Inject styles
    injectStyles();
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', createButton);
    } else {
      createButton();
    }
  }

  // Initialize
  init();

  // ============================================
  // PUBLIC API
  // ============================================
  window.RewardsButton = {
    // Update button configuration
    updateConfig: function(newConfig) {
      Object.assign(window.RewardsButtonConfig, newConfig);
      createButton();
    },
    
    // Show button
    show: function() {
      const button = document.getElementById('rewards-button-standalone');
      if (button) {
        button.style.display = 'flex';
      }
    },
    
    // Hide button
    hide: function() {
      const button = document.getElementById('rewards-button-standalone');
      if (button) {
        button.style.display = 'none';
      }
    },
    
    // Destroy button
    destroy: function() {
      const button = document.getElementById('rewards-button-standalone');
      if (button) {
        button.remove();
      }
      const styles = document.getElementById('rewards-button-styles');
      if (styles) {
        styles.remove();
      }
    }
  };

})();

